import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { origemDoEstado } from "@/rotas";
import { useFocoNoTitulo, useTituloDoDocumento } from "./useTituloDoDocumento";

/*
 * A página de uma entidade: quem tem abas e formulário (design D1). O que é leitura
 * com uma ou duas ações continua em `PainelLateral`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ UM TÍTULO, E SUBTÍTULO SÓ COM DADO.                                      │
 * │                                                                          │
 * │ Regra do dono do produto, cobrada mais de uma vez: `subtitulo` recebe    │
 * │ documento e banco. Situação é do selo. Frase explicando a tela não entra │
 * │ aqui, nem em lugar nenhum: o que precisa de explicação vai em `Dica`.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * A migalha é só para leitor de tela: à vista, seria o título repetido logo acima dele, e
 * um segundo link para a lista ao lado do "« voltar". Quem quer a lista sem filtro tem o
 * item da barra lateral.
 *
 * A estrutura vem da GUI da TecnoSpeed ("« voltar", título, selo). A aparência é a
 * dos docs 06a e 06b.
 *
 * O foco vai para o `h1` quando o título chega, e não na troca de aba: quem anda
 * pelas abas com o teclado continua nelas. Quem abre outro cedente remonta a página
 * com `key`, e o foco vai de novo.
 */
export function PaginaDeEntidade({
  secao,
  voltarPara,
  titulo,
  selo,
  subtitulo,
  acoes,
  children,
}: {
  /** O nome da seção: primeiro nível da migalha e parte do título do documento. */
  secao: string;
  /** A lista. O "« voltar" prefere a lista de onde o operador veio, com os filtros. */
  voltarPara: string;
  /** `undefined` enquanto carrega: o cabeçalho reserva a altura final. */
  titulo: string | undefined;
  /** O selo de situação, ao lado do título. */
  selo?: ReactNode;
  /** Só dado, e sem a situação, que é do selo. Nunca frase. */
  subtitulo?: ReactNode;
  acoes?: ReactNode;
  children: ReactNode;
}) {
  const estado: unknown = useLocation().state;
  const carregada = titulo !== undefined;
  const cabecalho = useFocoNoTitulo(carregada);

  useTituloDoDocumento(carregada ? `${titulo} · ${secao}` : secao);

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-2">
        <nav aria-label="Migalha" className="flex items-center gap-3 text-[12px]">
          <Link
            to={origemDoEstado(estado) ?? voltarPara}
            className="text-texto-2 underline-offset-2 hover:text-texto hover:underline"
          >
            <span aria-hidden="true">« </span>voltar
          </Link>

          <ol className="sr-only">
            <li>
              <Link to={voltarPara}>{secao}</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{titulo}</li>
          </ol>
        </nav>

        <div className="flex flex-wrap items-center gap-3">
          <h1
            ref={cabecalho}
            tabIndex={-1}
            aria-busy={!carregada}
            className="min-h-[22px] text-titulo font-semibold focus:outline-none"
          >
            {titulo}
          </h1>
          {selo}
          {acoes !== undefined && <div className="ml-auto flex items-center gap-2">{acoes}</div>}
        </div>

        <div className="dados min-h-[18px] text-[12px] text-texto-2">{subtitulo}</div>
      </header>

      {children}
    </section>
  );
}
